/**
 * Helper to fire toast alerts when an expense pushes a category's budget
 * past 80% (warning) or 100% (exceeded).
 *
 * Called after a successful expense POST. It does a single GET to
 * `/api/budgets/usage?month=&year=` and inspects the matching category.
 *
 * When the projected end-of-month spending exceeds the limit, the toast
 * description includes a predictive insight (linear projection):
 *   "A este ritmo terminarás el mes con $X de déficit en {categoría}."
 */
import api from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function buildProjection({ spent, monthly_limit, year, month, currency }) {
  const today = new Date();
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() + 1 === month;
  const dom = daysInMonth(year, month);
  const dayOfMonth = isCurrentMonth ? today.getDate() : dom;
  if (dayOfMonth <= 0 || spent <= 0) return null;
  const projected = (spent / dayOfMonth) * dom;
  const deficit = projected - monthly_limit;
  if (deficit <= 0) return null;
  return `A este ritmo terminarás el mes con ${formatCurrency(
    deficit,
    currency
  )} de déficit (proyección: ${formatCurrency(projected, currency)} para el día ${dom}).`;
}

export async function maybeAlertBudgetForExpense({ category, date }) {
  if (!category || !date) return;
  try {
    const [y, m] = date.split("-");
    const year = Number(y);
    const month = Number(m);
    if (!year || !month) return;
    const r = await api.get(`/budgets/usage?month=${month}&year=${year}`);
    const item = (r.data?.budgets || []).find((b) => b.category === category);
    if (!item) return;
    const currency = r.data?.currency || "MXN";
    const projection = buildProjection({
      spent: item.spent,
      monthly_limit: item.monthly_limit,
      year,
      month,
      currency,
    });
    if (item.status === "exceeded") {
      toast.error(
        `Presupuesto rebasado en ${category}: ${formatCurrency(
          item.spent,
          currency
        )} de ${formatCurrency(item.monthly_limit, currency)} (${item.pct}%)`,
        { description: projection || undefined, duration: 7000 }
      );
    } else if (item.status === "warning") {
      toast.warning(
        `Llevas ${item.pct}% del presupuesto de ${category} (${formatCurrency(
          item.spent,
          currency
        )} de ${formatCurrency(item.monthly_limit, currency)})`,
        { description: projection || undefined, duration: 6000 }
      );
    }
  } catch (_) {
    // silent — alerts are best-effort
  }
}
